import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_ACCOUNTS,
  RECEIVE_ACCOUNT,
  RECEIVE_ACCOUNT_METADATA,
  RECEIVE_ACCOUNT_FAILURE,
  RECEIVING_ACCOUNTS,
  SAVING_ACCOUNT,
  SAVING_ACCOUNT_FAILURE,
  UPDATING_ACCOUNT,
  UPDATING_ACCOUNT_FAILURE,
  DELETE_ACCOUNT,
  DELETING_ACCOUNT,
  DELETING_ACCOUNT_FAILURE,
  INCREMENT_ACCOUNT_COUNT,
  RESET_ACCOUNT_META,
  CLEAR_ACCOUNT_STATE,
  CLEAR_STATE,
  RECEIVE_DEFAULT_ACCOUNTS
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { receiveData, deleteData, increment, receiveMetadata, resetMetadata, clearLocalState, clearState } from '@/store/modules/helpers/mutationHelpers'

const accountSchema = new schema.Entity('accounts')
const accountListSchema = new schema.Array(accountSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false
}

const actions = {
  fetchAccountsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_ACCOUNTS, true)
    return Vue.http.get(buildUrlWithQuery('accounts', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_ACCOUNTS, false)
        this.commit(RESET_ACCOUNT_META)
        if (!r.hasOwnProperty('results')) return Promise.reject('Error fetching accounts')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, accountListSchema)
        if (!result || !Array.isArray(result)) return Promise.reject('Error normalizing response')
        this.commit(RECEIVE_ACCOUNTS, { data: entities.accounts })
        this.commit(RECEIVE_ACCOUNT_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_ACCOUNTS, false)
        this.commit(RECEIVE_ACCOUNT_FAILURE, handleXHRError(e))
      })
  },
  fetchDefaultAccounts (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('accounts/default', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r.hasOwnProperty('results')) return Promise.reject('Error fetching default accounts')
        const { results } = r
        const { entities, result } = normalize(results, accountListSchema)
        if (!result || !Array.isArray(result)) return Promise.reject('Error normalizing response')
        this.commit(RECEIVE_DEFAULT_ACCOUNTS, { data: entities && entities.accounts || [] })
        return { entities }
      })
      .catch(e => {
        return Promise.reject(handleXHRError(e))
      })
  },
  fetchAccountOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_ACCOUNTS, true)
    const headers = getHeaders(this.getters.getToken)
    return Vue.http({ url: buildUrl('accounts', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities, result } = normalize(choices, accountListSchema)
          if (!result || !Array.isArray(result)) return Promise.reject('Error normalizing response')
          this.commit(RECEIVE_ACCOUNTS, { data: entities.accounts })
          this.commit(RECEIVING_ACCOUNTS, false)
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_ACCOUNTS, false)
        this.commit(RECEIVE_ACCOUNT_FAILURE, handleXHRError(e))
      })
  },
  fetchAccountById (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_ACCOUNTS, true)
    return Vue.http.get(buildUrl(`accounts/${id}`, null, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_ACCOUNTS, false)
        if (!(r && r.id)) return this.commit(RECEIVE_ACCOUNT_FAILURE, 'Error fetching Account')
        this.commit(RECEIVE_ACCOUNT, r)
        return r
      })
      .catch(e => {
        this.commit(RECEIVING_ACCOUNTS, false)
        this.commit(RECEIVE_ACCOUNT_FAILURE, handleXHRError(e))
      })
  },
  resetAccountMeta () {
    return this.commit(RESET_ACCOUNT_META)
  },
  clearAccountState (store, clearCache = false) {
    this.commit(CLEAR_ACCOUNT_STATE, clearCache)
  }
}

const getters = {
  getAccounts: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllAccounts: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getAccountMetadata: (state) => {
    const { shown, count, previous, next } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, previous, next }
  },
  getAccountById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAccountFetchStatus: (state) => {
    return state.receivingData
  },
  getAccountSaveStatus: (state) => {
    return state.saving
  },
  getAccountSaveFailure: (state) => {
    return state.saveFailure
  },
  getAccountUpdateStatus: (state) => {
    return state.updating
  },
  getAccountUpdateFailure: (state) => {
    return state.updateFailure
  },
  getCurrentAccountPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [RECEIVE_ACCOUNTS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_ACCOUNTS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_ACCOUNT] (state, data) {
    const id = data.id
    if (!state.data) state.data = {}
    state.data[id] = data
    state.cache[id] = data
    return true
  },
  [RECEIVE_ACCOUNT_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_ACCOUNT] (state, payload) {
    state.saving = payload
  },
  [SAVING_ACCOUNT_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_ACCOUNT] (state, payload) {
    state.updating = payload
  },
  [UPDATING_ACCOUNT_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_ACCOUNT] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_ACCOUNT] (state, payload) {
    state.deleting = payload
  },
  [DELETING_ACCOUNT_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { status: false, id, err }
  },
  [INCREMENT_ACCOUNT_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_ACCOUNT_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_ACCOUNT_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_ACCOUNT_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/account.js